// FILE: src/pages/StudentCompleteInfoPage.jsx
// "Before you start" (plan WP-8): the one-time step MainLayout shows until
// the student has a mailing address and has given consent. Canadian address
// fields with browser-autofill tokens, plus Google address suggestions on the
// street field when configured (StreetAddressField); the consent text says what we collect and
// why. The server checks the postal code (A1A 1A1) and province and answers
// 400 VALIDATION_FAILED { problems: [{ field, problem }] }; those problems
// are shown on the fields. Other failures go through errorMessage.
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { fetchProfile, updateAddressAndConsent } from "../features/profileSlice";
import { ErrorState, LoadingState, PageHeader } from "../components/ui";
import { PROVINCES, normalisePostalCode, provinceCode } from "../components/student/provinces";
import StreetAddressField from "../components/student/StreetAddressField";
import { errorBody, errorCode, errorMessage } from "../i18n/errors";

const SERVER_FIELD = {
  "address.street": "street",
  "address.city": "city",
  "address.province": "province",
  "address.postalCode": "postalCode",
  address: "street",
  hasConsent: "consent"
};

const FIELD_ORDER = ["street", "city", "province", "postalCode", "consent"];

function problemText(field, problem) {
  const tooLong = problem === "too long";
  switch (field) {
    case "street":
      return tooLong ? "Use 120 characters or fewer." : "Check your street address.";
    case "city":
      return tooLong ? "Use 60 characters or fewer." : "Enter your city or town.";
    case "province":
      return "Choose your province or territory.";
    case "postalCode":
      return problem === "required" ? "Enter your postal code." : "Enter a Canadian postal code, like A1A 1A1.";
    case "consent":
      return "Check the box to give your consent.";
    default:
      return "Check this field.";
  }
}

const STEPS = [
  { label: "Account created", state: "done" },
  { label: "Address and consent", state: "current" },
  { label: "Your first lesson", state: "next" }
];

function StepNote() {
  return (
    <Box
      component="ol"
      aria-label="Getting started"
      sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexWrap: "wrap", gap: { xs: 1, sm: 3 }, mb: 3 }}
    >
      {STEPS.map((s, i) => {
        const Icon = s.state === "done" ? CheckCircleIcon : s.state === "current" ? PlaceOutlinedIcon : PlayCircleOutlineIcon;
        return (
          <Box
            component="li"
            key={s.label}
            aria-current={s.state === "current" ? "step" : undefined}
            sx={{ display: "flex", alignItems: "center", gap: 1, color: s.state === "next" ? "text.secondary" : "text.primary" }}
          >
            <Icon aria-hidden sx={{ color: s.state === "next" ? "text.secondary" : "primary.main" }} />
            <Typography component="span" sx={{ fontWeight: s.state === "current" ? 650 : 500, color: "inherit" }}>
              {`${i + 1}. ${s.label}`}
              {s.state === "done" ? " (done)" : s.state === "current" ? " (now)" : ""}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function StudentCompleteInfoPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile, loading, error } = useSelector((state) => state.profile);

  const saved = profile?.address || {};
  const [street, setStreet] = useState(saved.street || "");
  const [city, setCity] = useState(saved.city || "");
  const [province, setProvince] = useState(provinceCode(saved.province) || "ON");
  const [postalCode, setPostalCode] = useState(saved.postalCode || "");
  const [hasConsent, setHasConsent] = useState(Boolean(profile?.hasConsent));

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const refs = {
    street: useRef(null),
    city: useRef(null),
    province: useRef(null),
    postalCode: useRef(null),
    consent: useRef(null)
  };

  // If the profile arrives after the first render, start from what's saved.
  const prefilled = useRef(Boolean(profile));
  useEffect(() => {
    if (prefilled.current || !profile) return;
    prefilled.current = true;
    const a = profile.address || {};
    setStreet(a.street || "");
    setCity(a.city || "");
    setProvince(provinceCode(a.province) || "ON");
    setPostalCode(a.postalCode || "");
    setHasConsent(Boolean(profile.hasConsent));
  }, [profile]);

  // On mount, ensure we have the profile
  useEffect(() => {
    if (!profile && !loading && !error) {
      dispatch(fetchProfile());
    }
  }, [dispatch, profile, loading, error]);

  // Already complete → the dashboard.
  useEffect(() => {
    if (profile && profile.address && profile.hasConsent) {
      navigate("/student");
    }
  }, [profile, navigate]);

  const focusFirst = (errs) => {
    const first = FIELD_ORDER.find((f) => errs[f]);
    if (first) refs[first].current?.focus();
  };

  const clearError = (field) => setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));

  // A Google address suggestion was chosen: fill what it gave us.
  const fillAddress = (a) => {
    if (a.street) setStreet(a.street);
    if (a.city) setCity(a.city);
    if (a.province) setProvince(a.province);
    if (a.postalCode) setPostalCode(a.postalCode);
    setErrors((e) => {
      const next = { ...e };
      for (const f of ["street", "city", "province", "postalCode"]) if (a[f]) next[f] = undefined;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const next = {};
    if (!city.trim()) next.city = problemText("city", "required");
    if (!province) next.province = problemText("province", "required");
    const pc = normalisePostalCode(postalCode);
    if (!postalCode.trim()) next.postalCode = problemText("postalCode", "required");
    else if (!pc) next.postalCode = problemText("postalCode", "invalid format");
    if (!hasConsent) next.consent = problemText("consent", "required");
    setErrors(next);
    if (Object.keys(next).length) {
      focusFirst(next);
      return;
    }

    setSubmitting(true);
    const payload = {
      address: { street: street.trim(), city: city.trim(), province, postalCode: pc },
      hasConsent
    };
    const resultAction = await dispatch(updateAddressAndConsent(payload));
    setSubmitting(false);

    if (updateAddressAndConsent.fulfilled.match(resultAction)) {
      navigate("/student");
      return;
    }
    const failure = resultAction.payload || resultAction.error;
    if (errorCode(failure) === "VALIDATION_FAILED") {
      const fieldErrors = {};
      for (const p of errorBody(failure).problems || []) {
        const field = SERVER_FIELD[p?.field];
        if (field && !fieldErrors[field]) fieldErrors[field] = problemText(field, p.problem);
      }
      setErrors(fieldErrors);
      focusFirst(fieldErrors);
    }
    setSubmitError(failure || "INTERNAL_ERROR");
  };

  if (!profile && error) {
    return (
      <Box sx={{ maxWidth: 760, mx: "auto" }}>
        <PageHeader title="Before you start" />
        <ErrorState error={error} title="We couldn't load your profile" onRetry={() => dispatch(fetchProfile())} />
      </Box>
    );
  }
  if (!profile) return <LoadingState variant="page" label="Loading your profile…" />;

  return (
    <Box sx={{ maxWidth: 760, mx: "auto" }}>
      <PageHeader
        eyebrow="Welcome to your course"
        title="Before you start"
        description="We need your mailing address and your consent to keep your course records. It takes about a minute, and you only do it once."
      />

      <Card>
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <StepNote />

          <Stack component="form" spacing={3} onSubmit={handleSubmit} noValidate>
            {submitError && (
              <Alert severity="error" role="alert">
                {errorMessage(submitError)}
              </Alert>
            )}

            <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
              <Box component="legend" sx={{ p: 0, mb: 0.5 }}>
                <Typography component="h2" variant="h3">
                  Your mailing address
                </Typography>
              </Box>
              <Typography sx={{ color: "text.secondary", mb: 2.5 }}>Where you live in Canada.</Typography>
              <Stack spacing={2.5}>
                <StreetAddressField
                  id="address-street"
                  name="street"
                  label="Street address"
                  inputRef={refs.street}
                  value={street}
                  onChange={(text) => {
                    setStreet(text);
                    clearError("street");
                  }}
                  onAddressSelected={fillAddress}
                  error={Boolean(errors.street)}
                  helperText={errors.street || "Optional. For example: 123 Main St, Apt 4"}
                  maxLength={120}
                />
                <TextField
                  id="address-city"
                  name="city"
                  label="City or town"
                  inputRef={refs.city}
                  autoComplete="address-level2"
                  required
                  fullWidth
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    clearError("city");
                  }}
                  error={Boolean(errors.city)}
                  helperText={errors.city}
                  slotProps={{ htmlInput: { maxLength: 60 } }}
                />
                <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 3fr) minmax(0, 2fr)" } }}>
                  <FormControl required fullWidth error={Boolean(errors.province)}>
                    <InputLabel id="address-province-label" htmlFor="address-province">
                      Province or territory
                    </InputLabel>
                    <Select
                      native
                      labelId="address-province-label"
                      id="address-province"
                      label="Province or territory"
                      value={province}
                      onChange={(e) => {
                        setProvince(e.target.value);
                        clearError("province");
                      }}
                      inputRef={refs.province}
                      inputProps={{
                        name: "province",
                        autoComplete: "address-level1",
                        "aria-describedby": errors.province ? "address-province-error" : undefined
                      }}
                    >
                      <option value="" disabled>
                        Choose…
                      </option>
                      {PROVINCES.map((p) => (
                        <option key={p.code} value={p.code}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                    {errors.province && <FormHelperText id="address-province-error">{errors.province}</FormHelperText>}
                  </FormControl>
                  <TextField
                    id="address-postal-code"
                    name="postalCode"
                    label="Postal code"
                    inputRef={refs.postalCode}
                    autoComplete="postal-code"
                    required
                    fullWidth
                    value={postalCode}
                    onChange={(e) => {
                      setPostalCode(e.target.value.toUpperCase());
                      clearError("postalCode");
                    }}
                    onBlur={() => {
                      const pc = normalisePostalCode(postalCode);
                      if (pc) setPostalCode(pc);
                    }}
                    error={Boolean(errors.postalCode)}
                    helperText={errors.postalCode || "Format: A1A 1A1"}
                    slotProps={{
                      htmlInput: { inputMode: "text", autoCapitalize: "characters", spellCheck: false, maxLength: 7 }
                    }}
                  />
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box component="section" aria-labelledby="consent-title">
              <Typography id="consent-title" component="h2" variant="h3" sx={{ mb: 1 }}>
                Your consent
              </Typography>
              <Box id="consent-text" sx={{ p: 2, borderRadius: 3, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ mb: 1 }}>To give you this course, we collect and keep:</Typography>
                <Box component="ul" sx={{ m: 0, pl: 3, "& li": { mb: 0.5 } }}>
                  <Typography component="li">
                    your identity details, to confirm it's really you and to keep the course records the Ontario
                    Ministry of Transportation (MTO) requires;
                  </Typography>
                  <Typography component="li">your lesson time, progress and test results;</Typography>
                  <Typography component="li">your support requests and our replies.</Typography>
                </Box>
                <Typography sx={{ mt: 1 }}>
                  We keep these records for as long as the law requires. To see or correct your record, contact
                  student support.
                </Typography>
              </Box>
              <FormControl error={Boolean(errors.consent)} sx={{ mt: 1.5 }}>
                <FormControlLabel
                  required
                  sx={{ alignItems: "flex-start", mr: 0, "& .MuiFormControlLabel-asterisk": { ml: 0.5 } }}
                  control={
                    <Checkbox
                      checked={hasConsent}
                      inputRef={refs.consent}
                      onChange={(e) => {
                        setHasConsent(e.target.checked);
                        clearError("consent");
                      }}
                      slotProps={{
                        input: {
                          "aria-describedby": errors.consent ? "consent-text consent-error" : "consent-text"
                        }
                      }}
                      sx={{ mt: -0.75 }}
                    />
                  }
                  label="I agree that my information can be collected and kept as described above, and I confirm my address is correct."
                />
                {errors.consent && <FormHelperText id="consent-error">{errors.consent}</FormHelperText>}
              </FormControl>
            </Box>

            <Box>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" aria-hidden /> : null}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {submitting ? "Saving…" : "Save and continue"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
